/**
 * slErrors — Typed error classes for Pillar 3 (FE-side typed-fetch) per
 * ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2.
 *
 * Two distinct error surfaces:
 *
 *   1. `ContractMismatchError` — runtime envelope shape drift detected by
 *      Pillar 2 zod safeParse. Thrown by `viewGetTyped` / `getTyped` /
 *      `mutateTyped` when the wire envelope returned by SL does not match
 *      the zod schema authored against the canonical wire contract. Carries
 *      `slug`, `expectedShape`, `actualShape`, `rawData` for ContractMismatch
 *      forensics (so a Sentry tag or test assertion can pinpoint which
 *      envelope drifted on which slug).
 *
 *      D-33 R1 ratchet: NEVER returned. Always thrown. No silent fallback,
 *      no `as any` swallow.
 *
 *   2. `SLApiError` — typed HTTP non-2xx returned by SL. Wraps the
 *      `{detail: {error: {code, message}}}` envelope into a typed code +
 *      status surface. Distinct from `ServiceHttpError` (the underlying
 *      transport-level error) because `SLApiError` carries the *typed*
 *      `error_code` enum (per surface, generated from the wire schema)
 *      while `ServiceHttpError` is the open string-coded transport error.
 *      Mutations call SL through `mutateTyped` which translates the
 *      transport error into the typed surface here.
 *
 * See ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.3 row 1 (Pillar 3 ContractMismatch).
 */

/**
 * Thrown by `viewGetTyped`, `getTyped`, and `mutateTyped` when the wire
 * envelope returned by SL fails the consumer-side zod safeParse.
 *
 * D-33 R1: this error is the structural alternative to silent fallback.
 * If a consumer catches it, it must surface the drift (Sentry tag,
 * UI banner, test assertion) — never re-emit the raw `rawData` as if
 * it were valid.
 */
export class ContractMismatchError extends Error {
  readonly slug: string
  readonly expectedShape: string
  readonly actualShape: string
  readonly rawData: unknown

  constructor(args: {
    slug: string
    expectedShape: string
    actualShape: string
    rawData: unknown
    message?: string
  }) {
    super(
      args.message !== undefined
        ? args.message
        : `Contract mismatch on ${args.slug}: expected ${args.expectedShape}, got ${args.actualShape}`,
    )
    this.name = 'ContractMismatchError'
    this.slug = args.slug
    this.expectedShape = args.expectedShape
    this.actualShape = args.actualShape
    this.rawData = args.rawData
  }
}

/**
 * Thrown by `mutateTyped` (and may be thrown by `getTyped` for non-array
 * single-record reads) on HTTP non-2xx responses where the body matches
 * the typed-error envelope `{detail: {error: {code, message}}}`.
 *
 * `code` is the typed enum value the producer declared (e.g.
 * `'CHAT_INBOX_FORBIDDEN'`, `'DECISION_NOT_FOUND'`); `status` is the
 * HTTP status code; `message` is the human-readable producer message.
 *
 * When the body is non-typed (no `detail.error.code`), the caller falls
 * back to `code = 'UNKNOWN'` so the throw surface stays uniform.
 */
export class SLApiError extends Error {
  readonly code: string
  readonly status: number
  readonly slug?: string
  readonly details?: Record<string, unknown>

  constructor(args: {
    code: string
    status: number
    message: string
    slug?: string
    details?: Record<string, unknown>
  }) {
    super(args.message)
    this.name = 'SLApiError'
    this.code = args.code
    this.status = args.status
    this.slug = args.slug
    this.details = args.details
  }
}

/**
 * QueryCancelledError — thrown by `serviceFetch` when the *caller's*
 * AbortSignal aborts (the React-Query cancel contract: observer unmount,
 * route change, query refetch superseding the in-flight one).
 *
 * Why a dedicated class instead of re-throwing the raw `DOMException`
 * AbortError (BUG-FE-WIRE-UNCAUGHT-ABORTERROR-STORM-2026-06-07):
 *
 *   React Query's retryer swallows a queryFn rejection that arrives AFTER
 *   the query is already settled-as-cancelled (retryer.js: `if (isResolved())
 *   return`). But the SAME `serviceFetch` promise is also reached by
 *   non-retryer chains — `Promise.all` siblings inside a queryFn, un-awaited
 *   `queryClient.fetchQuery`/prefetch calls, and the persisted-query broadcast
 *   layer — and there the late rejection has NO consumer, so the browser logs
 *   "Uncaught (in promise) AbortError: signal is aborted without reason". On a
 *   Wochenplanung→Bridge transition that was 56→127 per session.
 *
 *   A caller-cancellation is NOT an error condition — it is the cancel contract
 *   working as designed. So the wire layer converts it into THIS typed,
 *   recognisable rejection and installs a global `unhandledrejection` guard
 *   (`installCancellationRejectionGuard`) that consumes exactly these — turning
 *   an UNHANDLED rejection into a HANDLED one. A genuine our-own-timeout abort
 *   is the opposite: it stays a loud `ServiceHttpError('TIMEOUT')`.
 *
 * `isQueryCancelledError` lets consumers branch on it; the global guard uses
 * it to suppress only our cancellations and nothing else.
 */
export class QueryCancelledError extends Error {
  readonly path?: string
  readonly method?: string

  constructor(args: { path?: string; method?: string; message?: string } = {}) {
    super(args.message !== undefined ? args.message : 'Request cancelled by caller (AbortSignal aborted)')
    this.name = 'QueryCancelledError'
    this.path = args.path
    this.method = args.method
  }
}

/** Type guard: was this a caller-cancellation from the SL wire layer? */
export function isQueryCancelledError(value: unknown): value is QueryCancelledError {
  return value instanceof QueryCancelledError
}

/** Type guard: a raw `DOMException` whose `name` is `'AbortError'`. */
export function isRawAbortError(value: unknown): value is DOMException {
  return (
    typeof DOMException !== 'undefined' &&
    value instanceof DOMException &&
    value.name === 'AbortError'
  )
}

/**
 * A rejection reason is a BENIGN cancellation — never a real failure — when it
 * is either our typed {@link QueryCancelledError} (the SL wire layer's converted
 * caller-cancel) OR a raw `DOMException` `AbortError`.
 *
 * Why include the raw AbortError (BUG-FE-BRIDGE-LOCALHOST-ABORT-STORM-2026-06-29):
 * `serviceFetch` converts the aborts it observes into `QueryCancelledError`, but
 * a raw `AbortError` can still reach `unhandledrejection` on an ORPHANED promise
 * when the conversion never runs — most notably during a Vite HMR full-page
 * reload or a browser navigation, where the browser aborts every in-flight fetch
 * (the bridge's 3s inbox/narration/manifest pollers) while the document is
 * tearing down. An AbortError is, by construction, the result of a requested
 * abort — it is NEVER a genuine bug, so silencing the orphaned (un-awaited)
 * instances is safe. This mirrors the existing RQ-layer contract in
 * `queryClient.ts` (`reportQueryError` already skips `DOMException` AbortError).
 *
 * Crucially this only changes UNHANDLED rejections (orphaned poll promises); any
 * AbortError that has an awaiter still rejects and surfaces wherever it is caught.
 * Real failures (`ServiceHttpError`, including `'TIMEOUT'`, `ContractMismatchError`,
 * `SLApiError`) are NOT AbortErrors, so they stay loud (D-33 / "no silent failures").
 */
export function isBenignCancellationRejection(value: unknown): boolean {
  return isQueryCancelledError(value) || isRawAbortError(value)
}

let cancellationGuardInstalled = false

/**
 * Install a process-/window-level `unhandledrejection` guard that silently
 * consumes benign cancellation rejections (see {@link isBenignCancellationRejection})
 * so a cancelled-mid-flight or torn-down-mid-flight request never surfaces as an
 * "Uncaught (in promise) AbortError" in the console. Idempotent and SSR-safe.
 *
 * It ONLY swallows benign cancellations: our own `QueryCancelledError` and raw
 * `DOMException` AbortErrors. Every other rejection — including
 * `ServiceHttpError('TIMEOUT')` and any real failure — is left untouched so
 * genuine bugs stay loud (D-33 / "no silent failures").
 *
 * Called once from the wire module (`serviceLayerClient`) on import; exposed
 * here so the abort-storm regression test can install it deterministically.
 */
export function installCancellationRejectionGuard(): void {
  if (cancellationGuardInstalled) return
  const target =
    typeof window !== 'undefined' && typeof window.addEventListener === 'function'
      ? window
      : undefined
  if (!target) return
  cancellationGuardInstalled = true
  target.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isBenignCancellationRejection(event.reason)) {
      // A caller-cancellation (or a teardown/HMR abort) is the cancel contract
      // working as designed, not a failure. Mark it handled so it never floods
      // the console. Real failures are never AbortErrors and stay loud.
      event.preventDefault()
    }
  })
}
